import { useState } from "react"
import type { LoopState } from "../types"
import { getLocationById, getUnitById } from "../game/content"
import {
  commandPointsUsed,
  getReleaseDestination,
  getStacksAtLocation,
} from "../game/armyEngine"
import { UnitPortrait, UnitSpecialty, UnitTraits } from "../components/UnitTraits"

interface ArmyScreenProps {
  loop: LoopState
  onTakeUnit: (unitId: string, count?: number) => void
  onTakeAllOfUnit: (unitId: string) => void
  onTakeAll: () => void
  onLeaveUnit: (unitId: string) => void
  onLeaveAllOfUnit: (unitId: string) => void
  onReleaseUnit: (unitId: string) => void
  onReleaseAllOfUnit: (unitId: string) => void
  onBack: () => void
}

export function ArmyScreen({
  loop,
  onTakeUnit,
  onTakeAllOfUnit,
  onTakeAll,
  onLeaveUnit,
  onLeaveAllOfUnit,
  onReleaseUnit,
  onReleaseAllOfUnit,
  onBack,
}: ArmyScreenProps) {
  // Releasing a whole contingent sends soldiers the player may have spent many
  // days collecting back across the map, and there is no undo — it warrants
  // one deliberate confirmation, unlike "Leave All" which parks them right here.
  const [pendingRelease, setPendingRelease] = useState<string | null>(null)
  const location = getLocationById(loop.currentLocationId)
  const waitingHere = getStacksAtLocation(loop, loop.currentLocationId)
  const used = commandPointsUsed(loop.currentArmy)
  const remaining = Math.max(0, loop.commandPoints - used)
  // Units parked elsewhere are still the player's to collect, but without a
  // listing they are effectively lost — the player would have to remember
  // every garrison across a whole loop.
  const garrisonsElsewhere = Object.entries(loop.armiesByLocation)
    .filter(([id, stacks]) => id !== loop.currentLocationId && stacks.length > 0)

  return (
    <div className="screen army-screen">
      <h1>Army</h1>

      <h2>Command Points</h2>
      <p>
        {used} / {loop.commandPoints} in use ({remaining} free)
      </p>

      <hr className="ornament-rule" />

      <h2>Your Army</h2>
      {loop.currentArmy.length === 0 ? (
        <p>No units are following you.</p>
      ) : (
        <ul className="stack-list">
          {loop.currentArmy.map((stack) => {
            const unit = getUnitById(stack.unitId)
            // Releasing sends soldiers home to their faction rather than
            // destroying them, so naming the destination keeps it clearly
            // distinct from leaving them parked here.
            const releaseDestinationId = getReleaseDestination(stack.unitId)
            const releaseDestination = releaseDestinationId
              ? getLocationById(releaseDestinationId)?.name ?? releaseDestinationId
              : null
            const releaseHint = releaseDestination
              ? ` (back to ${releaseDestination})`
              : " (disbanded)"
            return (
              <li key={stack.unitId} className="unit-row">
                <span className="unit-row-head">
                  {unit && <UnitPortrait unit={unit} size="md" />}
                  <span className="stack-name">
                    {unit?.name ?? stack.unitId} <strong>x{stack.count}</strong>
                  </span>
                </span>
                {unit && <UnitTraits unit={unit} />}
                {unit && <UnitSpecialty unit={unit} />}
                <span className="unit-row-actions">
                  <button type="button" onClick={() => onLeaveUnit(stack.unitId)}>
                    Leave 1
                  </button>{" "}
                  <button type="button" onClick={() => onLeaveAllOfUnit(stack.unitId)}>
                    Leave All
                  </button>{" "}
                  <button type="button" onClick={() => onReleaseUnit(stack.unitId)}>
                    Release 1{releaseHint}
                  </button>{" "}
                  {pendingRelease === stack.unitId ? (
                    <>
                      <button
                        type="button"
                        className="danger"
                        onClick={() => {
                          onReleaseAllOfUnit(stack.unitId)
                          setPendingRelease(null)
                        }}
                      >
                        Confirm: release all {stack.count}
                      </button>{" "}
                      <button type="button" onClick={() => setPendingRelease(null)}>
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setPendingRelease(stack.unitId)}
                    >
                      Release All{releaseHint}
                    </button>
                  )}
                </span>
              </li>
            )
          })}
        </ul>
      )}

      <hr className="ornament-rule" />

      <h2>Waiting at {location?.name ?? loop.currentLocationId}</h2>
      {waitingHere.length === 0 ? (
        <p>No units are waiting here.</p>
      ) : (
        <>
          <div className="action-list">
            <button type="button" onClick={onTakeAll} disabled={remaining <= 0}>
              Take All Possible
            </button>
          </div>
          <ul className="stack-list">
            {waitingHere.map((stack) => {
              const unit = getUnitById(stack.unitId)
              return (
                <li key={stack.unitId} className="unit-row">
                  <span className="unit-row-head">
                    {unit && <UnitPortrait unit={unit} size="md" />}
                    <span className="stack-name">
                      {unit?.name ?? stack.unitId} <strong>x{stack.count}</strong>
                    </span>
                  </span>
                  {unit && <UnitTraits unit={unit} />}
                  {unit && <UnitSpecialty unit={unit} />}
                  <span className="unit-row-actions">
                    <button
                      type="button"
                      onClick={() => onTakeUnit(stack.unitId)}
                      disabled={remaining <= 0}
                    >
                      Take 1
                    </button>{" "}
                    <button
                      type="button"
                      onClick={() => onTakeUnit(stack.unitId, 10)}
                      disabled={remaining <= 0}
                    >
                      Take 10
                    </button>{" "}
                    <button
                      type="button"
                      onClick={() => onTakeAllOfUnit(stack.unitId)}
                      disabled={remaining <= 0}
                    >
                      Take All
                    </button>
                  </span>
                </li>
              )
            })}
          </ul>
        </>
      )}

      <hr className="ornament-rule" />

      <h2>Waiting Elsewhere</h2>
      {garrisonsElsewhere.length === 0 ? (
        <p>You have left no units at other locations.</p>
      ) : (
        <ul className="stack-list">
          {garrisonsElsewhere.map(([id, stacks]) => (
            <li key={id}>
              {getLocationById(id)?.name ?? id}:{" "}
              {stacks
                .map(
                  (stack) =>
                    `${getUnitById(stack.unitId)?.name ?? stack.unitId} x${stack.count}`,
                )
                .join(", ")}
            </li>
          ))}
        </ul>
      )}

      <button type="button" onClick={onBack}>
        Back
      </button>
    </div>
  )
}
